import { useAccount, useIsAuthenticated, useMsal } from '@azure/msal-react'
import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import { acquireAccessToken } from '~/lib/auth/msal.ts'
import { rolesFromClaims, type AppRole } from '~/lib/auth/roles.ts'
import { isMockMode } from '~/lib/env.ts'

export interface CurrentUser {
  /** Entra object id. THE person linkage key — never the email, which is mutable and reusable. */
  oid: string
  name: string
  username: string
  /** Advisory only. See the banner in ~/lib/auth/roles.ts. */
  roles: readonly AppRole[]
}

/**
 * Die Nutzlast eines JWT, ohne Prüfung — nur zum Ein- und Ausblenden von Knöpfen.
 *
 * Geprüft wird das Token von der API, nicht hier. Ein Fehler beim Lesen heisst „keine Rollen".
 */
export function jwtNutzlast(token: string): Record<string, unknown> | undefined {
  try {
    const teil = token.split('.')[1]
    if (!teil) return undefined
    const b64 = teil.replace(/-/g, '+').replace(/_/g, '/')
    const json = atob(b64.padEnd(b64.length + ((4 - (b64.length % 4)) % 4), '='))
    const wert: unknown = JSON.parse(json)
    return typeof wert === 'object' && wert !== null ? (wert as Record<string, unknown>) : undefined
  } catch {
    return undefined
  }
}

/**
 * 🔴 DIE ROLLEN STEHEN IM ZUGRIFFSTOKEN DER API, NICHT IM ID-TOKEN.
 *
 * Die App-Rollen (Kassenwart, Vorstand, Prüfer) sind auf der API-Registrierung definiert. Ein
 * ID-Token wird für die SPA-Registrierung ausgestellt und trägt nur deren Rollen — und die hat
 * keine. Bis 2026-10-24 las die Oberfläche das ID-Token und hielt deshalb JEDEN für rollenlos:
 * „Person anlegen", „Bearbeiten", „Teilnehmer" und der ganze Bereich „Verwaltung" waren live für
 * alle unsichtbar, während die API dieselben Menschen als Kassenwart bediente.
 */
function useApiRollen(aktiv: boolean, kontoId: string | undefined) {
  return useQuery({
    queryKey: ['auth', 'api-rollen', kontoId],
    enabled: aktiv && !isMockMode,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const token = await acquireAccessToken()
      return token ? rolesFromClaims(jwtNutzlast(token)) : []
    },
  })
}

export function useCurrentUser(): CurrentUser | null {
  const { accounts } = useMsal()
  const isAuthenticated = useIsAuthenticated()
  const account = useAccount(accounts[0])
  const apiRollen = useApiRollen(isAuthenticated && !!account, account?.homeAccountId)

  return useMemo(() => {
    // Mock-Modus: ein erfundener Kassenwart, damit alle Ansichten ohne Backend begehbar sind.
    if (isMockMode) {
      return {
        oid: 'mock',
        name: 'Mock Kassenwart',
        username: 'mock@example.invalid',
        roles: ['Kassenwart', 'Vorstand'] as const,
      }
    }
    if (!isAuthenticated || !account) return null
    const claims = account.idTokenClaims as Record<string, unknown> | undefined
    const oid = typeof claims?.['oid'] === 'string' ? claims['oid'] : account.localAccountId
    const roles = [...new Set([...rolesFromClaims(claims), ...(apiRollen.data ?? [])])]
    return {
      oid,
      name: account.name ?? account.username,
      username: account.username,
      roles,
    }
  }, [isAuthenticated, account, apiRollen.data])
}

/**
 * `true` when the user's token carries any of `allowed`.
 *
 * 🔴 Use this to hide UI, never to protect anything. The API is the boundary.
 */
export function useHasRole(...allowed: readonly AppRole[]): boolean {
  const user = useCurrentUser()
  return user ? user.roles.some((role) => allowed.includes(role)) : false
}
